import React, { Component, PropsWithChildren, ReactNode, useEffect, useRef } from 'react';
import { useLogger } from '@amzn/xb-hooks';
import { useLocation } from 'react-router-dom';
import { AppLayout, Box, Header, SpaceBetween } from '@amzn/awsui-components-react';
import '../../pages/error/styles.scss';

const PAGE_LOAD_METRIC = 'PageComponent-Load';
const PAGE_FAIL_METRIC = 'PageComponent-Fail';

interface ErrorBoundaryProps {
    children: ReactNode;
    onError: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
    hasError: boolean;
}

/**
 * Class-based error boundary that emits PageComponent-Fail metric on crash.
 * React requires class components for error boundaries.
 */
export class PageErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.props.onError(error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div data-testid='tracked-page-error-fallback'>
                    <AppLayout
                        content={
                            <Box className='error-center'>
                                <SpaceBetween size='m'>
                                    <Box>
                                        <img src='/public/error_page.svg' alt='error' />
                                        <Header variant='h1'>Something went wrong</Header>
                                        <p>Please try refreshing the page.</p>
                                    </Box>
                                </SpaceBetween>
                            </Box>
                        }
                        toolsHide
                        navigationHide
                    />
                </div>
            );
        }
        return this.props.children;
    }
}

/**
 * TrackedPage wraps route content to emit XB availability metrics:
 * - `PageComponent-Load` on mount (success signal)
 * - `PageComponent-Fail` when a child throws during render (failure signal)
 *
 * These metrics are consumed by XbAppMonitoringStack to calculate availability.
 */
export const TrackedPage: React.FC<PropsWithChildren> = ({ children }) => {
    const { count } = useLogger();
    const location = useLocation();
    const lastEmittedPath = useRef<string | null>(null);

    useEffect(() => {
        if (lastEmittedPath.current !== location.pathname) {
            lastEmittedPath.current = location.pathname;
            count(PAGE_LOAD_METRIC);
        }
    });

    const handleError = (_error: Error, _errorInfo: React.ErrorInfo) => {
        count(PAGE_FAIL_METRIC);
    };

    return (
        <PageErrorBoundary key={location.pathname} onError={handleError}>
            {children}
        </PageErrorBoundary>
    );
};
